import isEmail from 'validator/lib/isEmail';
import isFQDN from 'validator/lib/isFQDN';
import isURL, { IsURLOptions } from 'validator/lib/isURL';
/**
 * Checks if the input string matches the regular expression for a valid email address
 *
 * @param {string} url The string to be validated
 * @returns {boolean} `true` if the input string corresponds to a valid email address and `false` otherwise
 */
const isValidEmail = function (url: string): boolean {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	return isEmail(url, { require_tld: false });
};

/**
 * returns domain for a valid email id.
 *
 * @param {string} url The string to be parsed
 * @returns {string} domain for valid email address otherwise throws error.
 */
const getEmailDomain = (address: string): string => {
	if (!isValidEmail(address)) {
		throw new Error('Invalid email id');
	}
	const parts = address.split('@');
	const domain = parts.pop();
	return domain;
};

/**
 * Checks if the input string matches the regular expression for a valid hostname
 *
 * @param {string} url The string to be validated
 * @returns {boolean} `true` if the input string corresponds to valid hostname and `false`
 */
const isValidDomainName = function (url: string): boolean {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	return isFQDN(url, { require_tld: false });
};

/**
 * Checks if the input string is a valid URL
 *
 * @param {string} url The input string to be validated
 * @returns {boolean} `true` if the `url` is a valid http/https URL and `false` otherwise
 */
const isValidUrl = function (url: string): boolean {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	const urlOptions: IsURLOptions = { protocols: ['http', 'https'], require_tld: false };
	try {
		const tempURL = new URL(url);
		return isURL(tempURL.toString(), urlOptions);
	} catch {
		return isURL(encodeURI(url), urlOptions);
	}
};

/**
 * Checks if input string is a valid URL with the HTTPS scheme
 *
 * @param {string} url input string representing a URL
 * @returns {boolean} `true` if `url` is a valid URL and has the HTTPS scheme, `false` for invalid URLs or URLs with other schemes
 */
const isHttpsUrl = function (url: string): boolean {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	const urlOptions: IsURLOptions = {
		protocols: ['https'],
		require_protocol: true,
		require_tld: false,
	};
	try {
		const tempURL = new URL(url);
		// eslint-disable-next-line @typescript-eslint/naming-convention
		return isURL(tempURL.toString(), urlOptions);
	} catch {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		return isURL(encodeURI(url), urlOptions);
	}
};

/**
 * Checks if input string is a valid URL with the HTTP scheme
 *
 * @param {string} url input string representing a URL
 * @returns {boolean} `true` if `url` is a valid URL and specifies the HTTP scheme, `false` for invalid URLs or URLs with other schemes
 */
const isHttpUrl = function (url: string): boolean {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	const urlOptions: IsURLOptions = {
		protocols: ['http'],
		require_protocol: true,
		require_tld: false,
	};
	try {
		const tempURL = new URL(url);
		// eslint-disable-next-line @typescript-eslint/naming-convention
		return isURL(tempURL.toString(), urlOptions);
	} catch {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		return isURL(encodeURI(url), urlOptions);
	}
};

/**
 *
 *
 * @param {string} url Input URL
 * @returns {string} `hostname` if the string is a valid URL, `null` otherwise.
 */
const getHostname = (url: string): string | null => {
	try {
		return new URL(url).hostname;
	} catch (err) {
		return null;
	}
};

/**
 *
 *
 * @param {string} url Input URL
 * @returns {string} `pathname` if the string is a valid URL, `null` otherwise.
 */
const getPathname = (url: string): string | null => {
	try {
		return new URL(url).pathname;
	} catch (err) {
		return null;
	}
};

/**
 * Adds the specified scheme to the input URL
 *
 * @param {string} urlStr
 * @param {string} scheme (without ://)
 * @param {boolean} [force=false] If true, overwrites the existing scheme, if any.
 * @return {*}  {string}
 */
const addSchemeToUrl = (urlStr: string, scheme: string, force = false): string => {
	try {
		const url = new URL(urlStr);
		if (force) {
			url.protocol = scheme;
			return url.toString();
		}
		if (!isHttpUrl(urlStr) && !isHttpsUrl(urlStr)) {
			return new URL(scheme.toLowerCase() + '://' + urlStr.toLowerCase()).toString();
		}
		return url.toString();
	} catch (_) {
		// url could just be a domain name
		return scheme.toLowerCase() + '://' + urlStr.toLowerCase();
	}
};

/**
 * Adds a path to the input URL after the domain name
 *
 * @param {string} urlStr input string representing a URL
 * @param {string} path path to be added to the URL (after the domain)
 * @param {boolean} force flag indicating if any existing path should be overwritten by _path_
 * @returns {string} a string representing a URL with the path appended as specified
 */
const addPathToUrl = (urlStr: string, path: string, force = false): string => {
	const url = new URL(urlStr);
	if (url.pathname === '/') {
		url.pathname = path;
		return url.toString();
	}
	if (force) {
		url.pathname = path;
	} else url.pathname += path;

	return url.toString();
};

/**
 * Compares if valid input urls are same by using URL API
 *
 * @param {string} url1
 * @param {string} url2
 * @return {*}  {boolean}
 */
function compareUrls(url1: string, url2: string): boolean {
	try {
		return new URL(url1).toString() === new URL(url2).toString();
	} catch (e) {
		return false;
	}
}

export default {
	isValidEmail,
	getEmailDomain,
	isValidDomainName,
	isValidUrl,
	isHttpsUrl,
	isHttpUrl,
	getHostname,
	addPathToUrl,
	addSchemeToUrl,
	getPathname,
	compareUrls,
};
